// ---------------------------------------------------------------------------
// Eingabezeile in natürlicher Sprache (PLAN.md §16c): kurze Sätze wie „Mutter von Lisa“, „stirbt in Die Schlacht“, „verfeindet mit @ben
// ab Der Verrat“ oder „Clara reist nach Rom“ werden zu einer Beziehung bzw. einer Zustandsänderung. Elemente stehen als Name oder als
// „@schlüssel“; fehlende werden gleich mit angelegt (Klasse aus dem Zusammenhang). Szenen stehen mit ihrem Titel (oder „2.3“) hinter
// „in“, „ab“, „seit“, „bis“. Wer gemeint ist, ergibt sich aus dem Satz oder aus dem Ort der Eingabe (Element bzw. Szene).
// ---------------------------------------------------------------------------

import type { StoryCtx } from './ctx.js';
import { entityTitle, narrativeOrder } from './narrative.js';
import { plainRefs, slugRef, takenRefKeys, uniqueRefKey } from './refs.js';
import { durationText, hasRelation, relationWord, sceneLabel } from './relations.js';
import { isScene } from './scene.js';
import { changesOf, checkStateChange, sameChange, stateRoleOf, type StateRole } from './states.js';
import { termsOf } from './terms.js';
import { germanNumber } from './ageText.js';
import type { Condition, RelationForm, RelationKind, StateKind } from './types.js';

/** Wo die Zeile steht: im Fenster eines Elements (es ist gemeint, wenn der Satz niemanden nennt) bzw. bei einer Szene (sie ist gemeint) */
export interface StatementContext {
  subjectId?: string | null;
  sceneId?: string | null;
}

/** Klassen, die die Eingabezeile für fehlende Elemente anlegt (vordefinierte Klassen, nach Name) */
export type NewEntityType = 'Person' | 'Ort' | 'Gegenstand' | 'Organisation';

export interface NewEntity {
  key: string;
  name: string;
  typeName: NewEntityType;
}

/** Ein Element der Aussage: vorhanden (`id`) oder neu anzulegen (`key` eines Eintrags in `create`) */
export type StatementRef = { id: string } | { key: string };

export type StatementOp =
  | { op: 'relation'; kind: RelationKind; source: StatementRef; target: StatementRef; from: string | null; to: string | null; forms: { source?: RelationForm; target?: RelationForm }; minutes?: number }
  | { op: 'change'; kind: StateKind; entity: StatementRef; value: StatementRef | Condition | string | null; sceneId: string; from?: StatementRef; trait?: string };

export interface ParsedStatement {
  op: StatementOp;
  create: NewEntity[];
  /** was daraus wird, als Satz („Clara: Mutter von Lisa“) */
  summary: string;
}

export type StatementResult = ParsedStatement | { error: string };

const ARTICLE = /^(?:der|die|das|den|dem|des|ein|eine|einen|einem|einer|eines|sein|seine|seinen|seinem|seiner|ihr|ihre|ihren|ihrem|ihrer)\s+/i;
const unquote = (s: string): string => s.replace(/^[„"“‚'»«]+|[“"”‘'«»]+$/g, '').trim();
const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

/** Szene nach Titel, „Kapitel.Szene“ oder „2.3 Titel“; bei mehreren passenden keine. */
export function sceneByTitle(ctx: StoryCtx, raw: string): string | null {
  const q = unquote(raw.replace(/^(?:der|die|das|dem|den)\s+szene\s+|^szene\s+/i, '')).toLowerCase();
  if (q === '') return null;
  const scenes = ctx.story.nodes.filter(isScene);
  const title = (id: string): string => String(ctx.nodes.get(id)?.fields.title ?? '').trim().toLowerCase();
  const labels = new Map(narrativeOrder(ctx).scenes.map((e) => [e.label, e.scene.id]));
  const byLabel = labels.get(q) ?? labels.get(q.split(' ')[0]!);
  if (byLabel && (q === q.split(' ')[0] || q.slice(q.indexOf(' ') + 1) === title(byLabel))) return byLabel;
  const exact = scenes.filter((s) => title(s.id) === q || title(s.id) === unquote(raw).toLowerCase());
  if (exact.length === 1) return exact[0]!.id;
  if (exact.length > 1 || q.length < 3) return null;
  const part = scenes.filter((s) => title(s.id).includes(q));
  return part.length === 1 ? part[0]!.id : null;
}

/** Element nach Name, Vorkommen oder Schlüssel (ohne Foundationals); sonst im Genitiv („Maras“, „des Vaters“, „Hans’“) */
function entityByName(ctx: StoryCtx, raw: string): string | null {
  const find = (q: string): string | null => {
    if (q === '') return null;
    for (const e of ctx.story.entities) {
      if (ctx.types.get(e.typeId)?.category === 'foundational') continue;
      if (e.refKey === q || termsOf(ctx, e).some((t) => t.toLowerCase() === q)) return e.id;
    }
    return null;
  };
  const q = raw.trim().toLowerCase();
  const genitive = q.replace(/(?:['’]s|['’]|e?s)$/u, '');
  return find(q) ?? (genitive !== q && genitive.length >= 2 ? find(genitive) : null);
}

/** „Geburt von Mara“, „Tod des Vaters“: das Nomen nach hinten („Mara Geburt“), dann liest es sich wie „Mara stirbt“ */
const NOUN_FIRST = /^(?:die\s+|der\s+)?(geburt|tod)\s+(?:von\s+|vom\s+)?(.+)$/iu;

/** Ein Satzteil, der ein Element nennt */
interface Slot {
  phrase: string;
  /** Klasse, falls es neu angelegt wird */
  type: NewEntityType;
}

interface Pred {
  re: RegExp;
  build: (m: RegExpMatchArray) => Draft | null;
}

type DraftRef = Slot | 'subject';
type Draft =
  | { op: 'relation'; kind: RelationKind; source: DraftRef; target: DraftRef; forms: { source?: RelationForm; target?: RelationForm }; event?: boolean; subjectType: NewEntityType; minutes?: number }
  | { op: 'change'; kind: StateKind; entity: DraftRef; value: DraftRef | Condition | { text: string } | null; subjectType: NewEntityType; from?: DraftRef; trait?: string };

/** Einheiten einer Dauer in Minuten */
const UNIT_MINUTES: [RegExp, number][] = [
  [/^(?:min|min\.|minute|minuten)$/i, 1],
  [/^(?:h|std|std\.|stunde|stunden)$/i, 60],
  [/^(?:tag|tage|tagen)$/i, 1440],
  [/^(?:woche|wochen)$/i, 10080],
  [/^(?:monat|monate|monaten)$/i, 43200],
];
const DURATION = '(\\d+(?:[.,]\\d+)?|[\\p{L}]+)\\s*(min\\.?|minuten?|h|std\\.?|stunden?|tage?n?|wochen?|monate?n?)';

/** Eine Dauer wie „3 Stunden“, „zwei Tage“, „1,5 h“ in Minuten; null, wenn es keine ist */
export function durationMinutes(amount: string, unit: string): number | null {
  const n = /^\d+(?:[.,]\d+)?$/.test(amount) ? Number(amount.replace(',', '.')) : (germanNumber(amount) ?? (/^(?:eine?n?)$/i.test(amount) ? 1 : null));
  const per = UNIT_MINUTES.find(([re]) => re.test(unit))?.[1];
  return n !== null && per && n > 0 ? Math.round(n * per) : null;
}

/** „Haare: grau“ – „keine“, „weg“, „–“ heißt: das Merkmal fällt weg */
const traitValue = (raw: string): { text: string } | null => {
  const v = unquote(raw.trim());
  return /^(?:keine?|weg|nichts|–|-)$/i.test(v) ? null : { text: v };
};

const FORM: Record<string, RelationForm | undefined> = {
  mutter: 'w', vater: 'm', tochter: 'w', sohn: 'm', schwester: 'w', bruder: 'm', ehefrau: 'w', ehemann: 'm', frau: 'w', mann: 'm', gattin: 'w', gatte: 'm',
  freundin: 'w', freund: 'm', feindin: 'w', feind: 'm', vorgesetzte: 'w', vorgesetzter: 'm', chefin: 'w', chef: 'm', herrin: 'w', herr: 'm',
  anführerin: 'w', anführer: 'm', untergebene: 'w', untergebener: 'm', dienerin: 'w', diener: 'm', magd: 'w', knecht: 'm',
};

const P = (s: string): RegExp => new RegExp(`^${s}$`, 'iu');
const IST = '(?:ist\\s+|war\\s+)?(?:die\\s+|der\\s+|das\\s+|ein\\s+|eine\\s+)?';
const person = (phrase: string): Slot => ({ phrase, type: 'Person' });
const rel = (kind: RelationKind, word: string | undefined, reverse: boolean, other: Slot, event = false): Draft => {
  const form = word ? FORM[word.toLowerCase()] : undefined;
  return reverse
    ? { op: 'relation', kind, source: other, target: 'subject', forms: form ? { target: form } : {}, event, subjectType: 'Person' }
    : { op: 'relation', kind, source: 'subject', target: other, forms: form ? { source: form } : {}, event, subjectType: 'Person' };
};
const cond = (value: Condition): Pred['build'] => () => ({ op: 'change', kind: 'verfassung', entity: 'subject', value, subjectType: 'Person' });

const PREDICATES: Pred[] = [
  // Beziehungen
  { re: P(`${IST}(mutter|vater|elternteil)\\s+(?:von\\s+)?(.+)`), build: (m) => rel('elternteil', m[1], false, person(m[2]!)) },
  { re: P(`${IST}(tochter|sohn|kind)\\s+(?:von\\s+)?(.+)`), build: (m) => rel('elternteil', m[1], true, person(m[2]!)) },
  { re: P(`${IST}(schwester|bruder|geschwister)\\s+(?:von\\s+)?(.+)`), build: (m) => rel('geschwister', m[1], false, person(m[2]!)) },
  { re: P(`${IST}(ehefrau|ehemann|frau|mann|gattin|gatte)\\s+von\\s+(.+)`), build: (m) => rel('ehe', m[1], false, person(m[2]!)) },
  { re: P(`(?:ist\\s+|war\\s+)?verheiratet\\s+mit\\s+(.+)`), build: (m) => rel('ehe', undefined, false, person(m[1]!)) },
  { re: P(`heiratet\\s+(.+)`), build: (m) => rel('ehe', undefined, false, person(m[1]!), true) },
  { re: P(`verliebt\\s+sich\\s+in\\s+(.+)`), build: (m) => rel('liebe', undefined, false, person(m[1]!), true) },
  { re: P(`(?:ist\\s+verliebt\\s+in|liebt)\\s+(.+)`), build: (m) => rel('liebe', undefined, false, person(m[1]!)) },
  { re: P(`${IST}(freundin|freund)\\s+von\\s+(.+)`), build: (m) => rel('freundschaft', m[1], false, person(m[2]!)) },
  { re: P(`(?:ist\\s+)?befreundet\\s+mit\\s+(.+)`), build: (m) => rel('freundschaft', undefined, false, person(m[1]!)) },
  { re: P(`freundet\\s+sich\\s+mit\\s+(.+?)\\s+an`), build: (m) => rel('freundschaft', undefined, false, person(m[1]!), true) },
  { re: P(`${IST}(feindin|feind)\\s+von\\s+(.+)`), build: (m) => rel('feindschaft', m[1], false, person(m[2]!)) },
  { re: P(`(?:ist\\s+)?verfeindet\\s+mit\\s+(.+)`), build: (m) => rel('feindschaft', undefined, false, person(m[1]!)) },
  { re: P(`(?:verfeindet\\s+sich\\s+mit|wendet\\s+sich\\s+gegen)\\s+(.+)`), build: (m) => rel('feindschaft', undefined, false, person(m[1]!), true) },
  { re: P(`hasst\\s+(.+)`), build: (m) => rel('feindschaft', undefined, false, person(m[1]!)) },
  { re: P(`${IST}(vorgesetzte|vorgesetzter|chefin|chef|herrin|herr|anführerin|anführer)\\s+(?:von\\s+)?(.+)`), build: (m) => rel('vorgesetzt', m[1], false, person(m[2]!)) },
  { re: P(`${IST}(untergebene|untergebener|dienerin|diener|magd|knecht)\\s+(?:von\\s+)?(.+)`), build: (m) => rel('vorgesetzt', m[1], true, person(m[2]!)) },
  { re: P(`dient\\s+(.+)`), build: (m) => rel('vorgesetzt', undefined, true, person(m[1]!)) },
  { re: P(`(?:ist\\s+)?(?:ein\\s+|eine\\s+)?mitglied\\s+(?:von|in|bei|im|der|des)\\s+(.+)`), build: (m) => rel('mitglied', undefined, false, { phrase: m[1]!, type: 'Organisation' }) },
  { re: P(`gehört\\s+zu\\s+(.+)`), build: (m) => rel('mitglied', undefined, false, { phrase: m[1]!, type: 'Organisation' }) },
  { re: P(`tritt\\s+(.+?)\\s+bei`), build: (m) => rel('mitglied', undefined, false, { phrase: m[1]!, type: 'Organisation' }, true) },
  // Verfassung (Geburt und Tod auch als Nomen: „Maras Geburt“, „Tod des Vaters“, siehe NOUN_FIRST)
  { re: P(`(?:(?:wird|wurde|ist|war)\\s+(?:.+\\s+)?geboren|geboren|(?:kommt|kam)\\s+(?:.+\\s+)?zur\\s+welt|erblickt\\s+das\\s+licht\\s+der\\s+welt|geburt)`), build: cond('geboren') },
  { re: P(`(?:stirbt|starb|verstirbt|fällt|ist\\s+tot|tot|tod|wird\\s+getötet|wird\\s+ermordet|wird\\s+hingerichtet|wird\\s+erschlagen|kommt\\s+ums\\s+leben)`), build: cond('tot') },
  { re: P(`(?:wird\\s+|ist\\s+)?(?:schwer\\s+|leicht\\s+)?(?:verletzt|verwundet)`), build: cond('verletzt') },
  { re: P(`(?:genest|erholt\\s+sich|ist\\s+(?:wieder\\s+)?gesund|wird\\s+(?:wieder\\s+)?gesund|wird\\s+geheilt|ist\\s+geheilt|gesund|lebt\\s+wieder)`), build: cond('gesund') },
  // Aufenthaltsort
  {
    re: P(`(?:reist|geht|fährt|flieht|flüchtet|zieht|kehrt|kommt|gelangt|eilt|läuft|reitet|segelt|wandert|ist|befindet\\s+sich|wohnt|lebt|bleibt)\\s+(?:zurück\\s+)?(?:nach|in|ins|im|zu|zum|zur|an|am|auf)\\s+(.+?)(?:\\s+(?:zurück|an))?`),
    build: (m) => ({ op: 'change', kind: 'ort', entity: 'subject', value: { phrase: m[1]!, type: 'Ort' }, subjectType: 'Person' }),
  },
  { re: P(`(?:erreicht|betritt)\\s+(.+)`), build: (m) => ({ op: 'change', kind: 'ort', entity: 'subject', value: { phrase: m[1]!, type: 'Ort' }, subjectType: 'Person' }) },
  // Besitz
  {
    re: P(`(?:gibt|übergibt|überreicht|schenkt|verkauft|reicht|vererbt|bringt)\\s+(.+?)\\s+an\\s+(.+)`),
    build: (m) => ({ op: 'change', kind: 'besitz', entity: { phrase: m[1]!, type: 'Gegenstand' }, value: person(m[2]!), subjectType: 'Person', from: 'subject' }),
  },
  {
    re: P(`(?:nimmt|ergreift|erhält|bekommt|findet|stiehlt|erbt|kauft|holt|hat|besitzt)\\s+(.+?)(?:\\s+an\\s+sich|\\s+auf)?`),
    build: (m) => ({ op: 'change', kind: 'besitz', entity: { phrase: m[1]!, type: 'Gegenstand' }, value: 'subject', subjectType: 'Person' }),
  },
  { re: P(`(?:verliert|verlor)\\s+(.+)`), build: (m) => ({ op: 'change', kind: 'besitz', entity: { phrase: m[1]!, type: 'Gegenstand' }, value: null, subjectType: 'Person', from: 'subject' }) },
  { re: P(`(?:geht|kommt|fällt)\\s+an\\s+(.+)`), build: (m) => ({ op: 'change', kind: 'besitz', entity: 'subject', value: person(m[1]!), subjectType: 'Gegenstand' }) },
  { re: P(`(?:ist\\s+|geht\\s+)?verloren`), build: () => ({ op: 'change', kind: 'besitz', entity: 'subject', value: null, subjectType: 'Gegenstand' }) },
  // Weg zwischen zwei Orten mit Reisezeit (AP-40): „Wien nach Linz: 3 Stunden“, „nach Linz sind es zwei Tage“
  {
    re: P(`(?:ist\\s+)?(?:der\\s+)?(?:weg\\s+)?nach\\s+(.+?):?\\s+(?:(?:sind\\s+es|dauert\\s+(?:es|der\\s+weg)|braucht\\s+man|in)\\s+)?(?:etwa\\s+|ungefähr\\s+|rund\\s+)?${DURATION}`),
    build: (m) => {
      const minutes = durationMinutes(m[2]!, m[3]!);
      return minutes ? { op: 'relation', kind: 'weg', source: 'subject', target: { phrase: m[1]!, type: 'Ort' }, forms: {}, subjectType: 'Ort', minutes } : null;
    },
  },
  // Merkmal (AP-31): „Haare: grau“, „Merkmal Narbe: linke Wange“, „seine Haare werden grau“, „die Narbe verschwindet“
  {
    re: P(`(?:merkmal\\s+)?(?:die\\s+|der\\s+|das\\s+|sein\\s+|seine\\s+|ihr\\s+|ihre\\s+)?([\\p{L}][\\p{L}-]*(?:\\s+[\\p{L}][\\p{L}-]*)?):\\s*(.+)`),
    build: (m) => ({ op: 'change', kind: 'merkmal', entity: 'subject', trait: cap(m[1]!), value: traitValue(m[2]!), subjectType: 'Person' }),
  },
  {
    re: P(`(?:die|der|das|sein|seine|ihr|ihre)\\s+([\\p{L}][\\p{L}-]*)\\s+(?:wird|werden|wurde|wurden)\\s+(.+)`),
    build: (m) => ({ op: 'change', kind: 'merkmal', entity: 'subject', trait: cap(m[1]!), value: traitValue(m[2]!), subjectType: 'Person' }),
  },
  {
    re: P(`(?:merkmal\\s+)?(?:die\\s+|der\\s+|das\\s+|sein\\s+|seine\\s+|ihr\\s+|ihre\\s+)?([\\p{L}][\\p{L}-]*)\\s+(?:entfällt|fällt\\s+weg|verschwindet|ist\\s+weg)`),
    build: (m) => ({ op: 'change', kind: 'merkmal', entity: 'subject', trait: cap(m[1]!), value: null, subjectType: 'Person' }),
  },
];

/** Satzteil „hinter in/ab/seit/bis“: Szene */
interface Clause {
  word: string;
  sceneId: string;
}

const CLAUSE_WORDS = new Set(['ab', 'seit', 'bis', 'in', 'im', 'bei', 'während']);

/**
 * Szenen am Ende abtrennen („… ab Der Verrat bis 3.2“): je Durchgang das vorderste Wort, hinter dem bis zum Ende eine Szene steht – so
 * bleibt ein Titel ganz („in Im Gefängnis“), und „ab X bis Y“ zerfällt von hinten.
 */
function splitClauses(ctx: StoryCtx, tokens: string[]): { rest: string[]; clauses: Clause[] } {
  const clauses: Clause[] = [];
  let rest = tokens;
  for (;;) {
    let hit: { i: number; clause: Clause } | null = null;
    for (let i = 1; i < rest.length - 1 && !hit; i++) {
      const word = rest[i]!.toLowerCase();
      const sceneId = CLAUSE_WORDS.has(word) ? sceneByTitle(ctx, rest.slice(i + 1).join(' ')) : null;
      if (sceneId) hit = { i, clause: { word, sceneId } };
    }
    if (!hit) return { rest, clauses };
    clauses.unshift(hit.clause);
    rest = rest.slice(0, hit.i);
  }
}

const ROLE_OF: Record<NewEntityType, StateRole> = { Person: 'person', Ort: 'place', Gegenstand: 'item', Organisation: 'other' };

/** Liest eine Zeile. Liefert, was daraus wird (samt neu anzulegender Elemente), oder eine Meldung. */
export function parseStatement(ctx: StoryCtx, input: string, context: StatementContext = {}): StatementResult {
  // „(Der Weg) von Wien nach Linz …“: der Ausgangsort ist das Subjekt
  const text = input
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/[.!]+$/, '')
    .replace(/^(?:der\s+)?(?:weg\s+)?von\s+(?=\S.*\snach\s)/i, '');
  if (text === '') return { error: 'Schreibe z. B. „Mutter von Lisa“, „stirbt in Die Schlacht“ oder „Clara reist nach Rom“.' };
  const split = splitClauses(ctx, text.split(' '));
  const clauses = split.clauses;
  const noun = split.rest.join(' ').match(NOUN_FIRST);
  const rest = noun ? [...noun[2]!.split(' '), noun[1]!] : split.rest;
  const subjectCtx = context.subjectId && ctx.entities.has(context.subjectId) ? context.subjectId : null;

  // wer: aus dem Ort der Eingabe (ganzer Satz ist die Aussage) oder die ersten Wörter
  const predicate = (words: string[]): Draft | null => {
    const s = words.join(' ');
    for (const p of PREDICATES) {
      const m = s.match(p.re);
      const draft = m ? p.build(m) : null;
      if (draft) return draft;
    }
    return null;
  };
  let found = subjectCtx ? predicate(rest) : null;
  let subject: string | null = null;
  for (let k = 1; !found && k <= Math.min(6, rest.length - 1); k++) {
    found = predicate(rest.slice(k));
    if (found) subject = rest.slice(0, k).join(' ');
  }
  if (!found) return { error: 'Nicht verstanden. Beispiele: „Mutter von Lisa“, „stirbt in Die Schlacht“, „verfeindet mit Ben ab Der Verrat“, „nimmt den Ring an sich“.' };
  const draft = found;
  if (subject === null && !subjectCtx) return { error: 'Wer? Beginne mit dem Namen, z. B. „Clara stirbt“.' };

  // Elemente auflösen bzw. neu anlegen
  const create: NewEntity[] = [];
  const taken = takenRefKeys(ctx);
  const resolve = (slot: Slot): StatementRef | { error: string } => {
    const raw = unquote(slot.phrase.trim());
    if (raw.startsWith('@')) {
      const key = raw.slice(1).toLowerCase();
      if (!/^[a-z][a-z0-9_]{0,29}$/.test(key)) return { error: `„${raw}“ ist kein gültiger Verweis.` };
      const e = ctx.story.entities.find((x) => x.refKey === key);
      if (e) return { id: e.id };
      const had = create.find((c) => c.key === key);
      if (!had) {
        create.push({ key, name: key.split('_').filter(Boolean).map(cap).join(' '), typeName: slot.type });
        taken.add(key);
      }
      return { key };
    }
    const id = entityByName(ctx, raw) ?? entityByName(ctx, raw.replace(ARTICLE, ''));
    if (id) return { id };
    const name = cap(raw.replace(ARTICLE, '').trim());
    if (name === '' || /[[\]@]/.test(name)) return { error: `„${raw}“ ist kein Name.` };
    const had = create.find((c) => c.name.toLowerCase() === name.toLowerCase());
    if (had) return { key: had.key };
    const key = uniqueRefKey(taken, slugRef(name));
    taken.add(key);
    create.push({ key, name, typeName: slot.type });
    return { key };
  };
  const subjectRef = subject !== null ? resolve({ phrase: subject, type: draft.subjectType }) : { id: subjectCtx! };
  if ('error' in subjectRef) return subjectRef;
  const ref = (r: DraftRef): StatementRef | { error: string } => (r === 'subject' ? subjectRef : resolve(r));

  const nameOf = (r: StatementRef): string => ('id' in r ? entityTitle(ctx, r.id) : create.find((c) => c.key === r.key)!.name);
  const roleOf = (r: StatementRef): StateRole => ('id' in r ? stateRoleOf(ctx, r.id) : ROLE_OF[create.find((c) => c.key === r.key)!.typeName]);
  const same = (a: StatementRef, b: StatementRef): boolean => ('id' in a && 'id' in b ? a.id === b.id : 'key' in a && 'key' in b && a.key === b.key);
  const sceneFor = (words: string[]): string | null => clauses.find((c) => words.includes(c.word))?.sceneId ?? null;
  const at = (id: string): string => `„${sceneLabel(ctx, id)}“`;
  const news = (): string => (create.length > 0 ? ` · neu: ${create.map((c) => `${c.name} (${c.typeName})`).join(', ')}` : '');

  if (draft.op === 'relation') {
    const source = ref(draft.source);
    if ('error' in source) return source;
    const target = ref(draft.target);
    if ('error' in target) return target;
    if (same(source, target)) return { error: 'Eine Beziehung braucht zwei verschiedene Entitäten.' };
    if ('id' in source && 'id' in target && hasRelation(ctx, source.id, draft.kind, target.id)) return { error: 'Diese Beziehung gibt es schon.' };
    const from = sceneFor(['ab', 'seit', 'in', 'im', 'bei', 'während']) ?? (draft.event ? (context.sceneId ?? null) : null);
    const to = sceneFor(['bis']);
    const own = same(subjectRef, source) ? 'source' : 'target';
    const form = own === 'source' ? draft.forms.source : draft.forms.target;
    const word = relationWord(draft.kind, own, form ?? null);
    const other = own === 'source' ? target : source;
    if (draft.kind === 'weg' && (roleOf(source) !== 'place' || roleOf(target) !== 'place')) return { error: 'Ein Weg verbindet zwei Orte.' };
    const minutes = draft.minutes ? ` (${durationText(draft.minutes)})` : '';
    const summary = `${nameOf(subjectRef)}: ${word} ${nameOf(other)}${minutes}${from ? ` · ab ${at(from)}` : ''}${to ? ` · bis ${at(to)}` : ''}${news()}`;
    return { op: { op: 'relation', kind: draft.kind, source, target, from, to, forms: draft.forms, ...(draft.minutes ? { minutes: draft.minutes } : {}) }, create, summary };
  }

  const sceneId = sceneFor(['in', 'im', 'ab', 'seit', 'bei', 'während']) ?? (context.sceneId && isScene(ctx.nodes.get(context.sceneId)) ? context.sceneId : null);
  if (!sceneId) return { error: 'In welcher Szene? Ergänze z. B. „in Die Schlacht“ (Titel der Szene).' };
  const entity = ref(draft.entity);
  if ('error' in entity) return entity;
  let value: StatementRef | Condition | string | null = null;
  if (draft.value !== null && typeof draft.value === 'object' && 'text' in draft.value) value = draft.value.text;
  else if (draft.value !== null && typeof draft.value === 'object') {
    const v = resolve(draft.value);
    if ('error' in v) return v;
    value = v;
  } else if (draft.value === 'subject') value = subjectRef;
  else value = draft.value;
  // Besitz: wer ihn weitergibt bzw. verliert
  const from = draft.from ? ref(draft.from) : null;
  if (from && 'error' in from) return from;
  const name = nameOf(entity);
  if (draft.kind === 'ort' && value && typeof value === 'object' && roleOf(value) !== 'place') return { error: `„${nameOf(value)}“ ist kein Ort.` };
  if (draft.kind === 'besitz') {
    if (roleOf(entity) === 'person' || roleOf(entity) === 'place') return { error: `„${name}“ ist kein Gegenstand – Besitz gilt für Gegenstände.` };
    if (value && typeof value === 'object' && same(value, entity)) return { error: 'Ein Gegenstand kann nicht bei sich selbst sein.' };
  }
  if ('id' in entity && (value === null || typeof value === 'string' || 'id' in value)) {
    const v = value === null || typeof value === 'string' ? value : value.id;
    const trait = draft.trait ? { trait: draft.trait } : {};
    const err = checkStateChange(ctx, { entityId: entity.id, kind: draft.kind, value: v, ...(from && 'id' in from ? { from: from.id } : {}), ...trait });
    if (err) return { error: err };
    if (changesOf(ctx.nodes.get(sceneId)!).some((c) => sameChange(c, { entityId: entity.id, kind: draft.kind, value: v, ...trait }))) return { error: 'Das steht schon in dieser Szene.' };
  }
  const what =
    draft.kind === 'merkmal'
      ? `${draft.trait}${value === null ? ' fällt weg' : `: ${String(value)}`}`
      : draft.kind === 'verfassung'
        ? String(value)
        : draft.kind === 'ort'
          ? `in ${nameOf(value as StatementRef)}`
          : value === null
            ? 'verloren'
            : `bei ${nameOf(value as StatementRef)}`;
  const summary = `${name}: ${what}${from ? ` (von ${nameOf(from)})` : ''} · in ${at(sceneId)}${news()}`;
  return { op: { op: 'change', kind: draft.kind, entity, value, sceneId, ...(from ? { from } : {}), ...(draft.trait ? { trait: draft.trait } : {}) }, create, summary };
}

/** Eine Änderung, die Titel bzw. Kurzbeschreibung einer Szene nahelegen (PF 15) */
export interface ChangeSuggestion {
  entityId: string;
  kind: StateKind;
  value: string | null;
  /** Besitz: wer ihn weitergibt bzw. verliert */
  from?: string;
  /** der Satz, aus dem sie stammt – so übernimmt ihn die Eingabezeile */
  text: string;
  source: 'title' | 'description';
}

/**
 * Was Titel und Kurzbeschreibung einer Szene an Änderungen nahelegen („Maras Geburt“, „Tod des Vaters“, „Clara reist nach Rom“) – gelesen
 * wie die Eingabezeile, nur mit vorhandenen Elementen und ohne, was die Szene schon festhält. Gedacht vor allem für Hintergrundereignisse,
 * deren Text nie geschrieben wird. Nur Vorschläge: festgehalten wird erst mit einem Klick.
 */
export function sceneSuggestions(ctx: StoryCtx, sceneId: string): ChangeSuggestion[] {
  const scene = ctx.nodes.get(sceneId);
  if (!scene || !isScene(scene)) return [];
  const title = String(scene.fields.title ?? '').trim();
  const sentences = plainRefs(String(scene.fields.description ?? ''))
    .split(/(?<=[.!?])\s+|\n+/)
    .map((s) => s.trim())
    .filter((s) => s !== '' && s.length <= 120)
    .slice(0, 6);
  const candidates = [...(title ? [{ text: title, source: 'title' as const }] : []), ...sentences.map((text) => ({ text, source: 'description' as const }))];
  const out: ChangeSuggestion[] = [];
  for (const c of candidates) {
    const r = parseStatement(ctx, c.text, { sceneId });
    if ('error' in r || r.op.op !== 'change' || r.op.kind === 'merkmal' || r.create.length > 0 || r.op.sceneId !== sceneId || !('id' in r.op.entity)) continue;
    const v = r.op.value;
    if (v !== null && typeof v === 'object' && !('id' in v)) continue;
    const from = r.op.from && 'id' in r.op.from ? r.op.from.id : undefined;
    const change = { entityId: r.op.entity.id, kind: r.op.kind, value: v === null || typeof v === 'string' ? v : v.id, ...(from ? { from } : {}) };
    if (!out.some((o) => sameChange(o, change))) out.push({ ...change, text: c.text, source: c.source });
  }
  return out;
}
